import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline } from './sectionHelpers';

interface Props {
  section: SectionInstance;
}

const MetricsLarge: React.FC<Props> = ({ section }) => {
  const c = section.controls;
  const variant = c.variant || 'light';

  const metrics = (c.metrics || [
    { number: '10K', label: 'Happy Customers', prefix: '', suffix: '+' },
    { number: '99.9', label: 'Uptime SLA', prefix: '', suffix: '%' },
    { number: '150', label: 'Countries Served', prefix: '', suffix: '+' },
    { number: '24', label: 'Support Hours', prefix: '', suffix: '/7' },
  ]) as Array<{ number: string; label: string; prefix?: string; suffix?: string }>;

  const isDark = variant === 'dark';
  const isGradient = variant === 'gradient';

  let bgStyle: React.CSSProperties = {};
  let textColor = 'var(--brand-text, #1e293b)';
  let labelColor = 'var(--brand-text-light, #64748b)';
  let numberColor = 'var(--brand-primary, #2563eb)';

  if (isDark) {
    bgStyle = { background: '#0f172a' };
    textColor = '#fff';
    labelColor = 'rgba(255,255,255,0.6)';
    numberColor = '#60a5fa';
  } else if (isGradient) {
    bgStyle = { background: 'linear-gradient(135deg, var(--brand-primary, #1a365d) 0%, var(--brand-secondary, #7c3aed) 100%)' };
    textColor = '#fff';
    labelColor = 'rgba(255,255,255,0.7)';
    numberColor = '#fff';
  } else {
    bgStyle = { background: 'var(--brand-bg-alt, #f8fafc)' };
  }

  return (
    <section
      className="sb-render sb-render-metrics-large"
      style={{ padding: '80px 24px', ...bgStyle }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {sfHeadline(c) && (
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <InlineEditText
              tag="h2"
              controlKey="headline"
              style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: 0, letterSpacing: '-0.025em', color: textColor }}
            >
              {sfHeadline(c)}
            </InlineEditText>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(metrics.length, 4)}, 1fr)`,
          gap: 0,
        }}>
          {metrics.map((metric, i) => (
            <div
              key={i}
              style={{
                padding: '40px 32px',
                textAlign: 'center',
                borderRight: i < metrics.length - 1 ? `1px solid ${isDark || isGradient ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` : 'none',
                position: 'relative',
              }}
            >
              {/* Subtle background accent for light variant */}
              {!isDark && !isGradient && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(37,99,235,0.05) 0%, transparent 70%)',
                  pointerEvents: 'none',
                }} />
              )}

              {/* Number */}
              <div style={{
                fontSize: '4.5rem',
                fontWeight: 900,
                lineHeight: 1,
                marginBottom: 12,
                color: numberColor,
                letterSpacing: '-0.03em',
                fontVariantNumeric: 'tabular-nums',
                position: 'relative',
              }}>
                {metric.prefix && (
                  <span style={{ fontSize: '2.5rem', verticalAlign: 'top', marginTop: 8, display: 'inline-block' }}>
                    {metric.prefix}
                  </span>
                )}
                {metric.number}
                {metric.suffix && (
                  <span style={{ fontSize: '2.5rem', verticalAlign: 'top', marginTop: 8, display: 'inline-block' }}>
                    {metric.suffix}
                  </span>
                )}
              </div>

              {/* Label */}
              <div style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: labelColor,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}>
                {metric.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MetricsLarge;
